import { DragEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { AssignedPerson, OperationTurno } from 'models/OperationRequest';

import { Blocker } from '../data/assignment';
import AssignedPersonChip from './AssignedPersonChip';

interface AssignmentTurnoBoxProps {
  turno: OperationTurno;
  people: AssignedPerson[];
  /** Hay alguien siendo arrastrado. */
  dragging: boolean;
  /** Arrastre desde otro turno (mover). */
  draggingFromKey: string | null;
  blocker: Blocker | null;
  focus: boolean;
  onDrop: () => void;
  onFocus: () => void;
  onChipDragStart: (
    person: AssignedPerson,
    e: DragEvent<HTMLSpanElement>
  ) => void;
  onDragEnd: () => void;
  onOpenPerson: (leadId: string) => void;
  onRemove: (leadId: string) => void;
}

function AssignmentTurnoBox({
  turno,
  people,
  dragging,
  draggingFromKey,
  blocker,
  focus,
  onDrop,
  onFocus,
  onChipDragStart,
  onDragEnd,
  onOpenPerson,
  onRemove,
}: AssignmentTurnoBoxProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.assign_people.board';

  const full = people.length >= turno.cupos;
  const canDrop =
    dragging && draggingFromKey !== turno.key && (!blocker || !!blocker.soft);
  const rejects = dragging && !canDrop && draggingFromKey !== turno.key;

  const classes = ['assignment-board__turno'];
  if (canDrop) {
    classes.push(
      blocker?.soft
        ? 'assignment-board__turno--soft-drop'
        : 'assignment-board__turno--can-drop'
    );
  } else if (rejects) classes.push('assignment-board__turno--reject');
  else if (focus) classes.push('assignment-board__turno--focus');
  else if (full) classes.push('assignment-board__turno--full');

  let countClass = 'assignment-board__turno-count';
  if (full) countClass += ' assignment-board__turno-count--full';
  else if (people.length)
    countClass += ' assignment-board__turno-count--partial';

  return (
    <div
      className={classes.join(' ')}
      role="button"
      tabIndex={0}
      aria-pressed={focus}
      onClick={onFocus}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onFocus();
        }
      }}
      onDragOver={(e) => {
        if (canDrop) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDrop();
      }}
    >
      <div className="assignment-board__turno-head">
        <span
          className={`assignment-board__block-tag${
            full ? ' assignment-board__block-tag--full' : ''
          }`}
        >
          {turno.block.toUpperCase()}
        </span>
        <span className="assignment-board__turno-time">
          {`${turno.from}–${turno.to}`}
        </span>
        <span className={countClass}>{`${people.length}/${turno.cupos}`}</span>
      </div>
      <div className="assignment-board__turno-people">
        {people.map((person) => (
          <div key={person.leadId}>
            <AssignedPersonChip
              compact
              person={person}
              onDragStart={(e) => onChipDragStart(person, e)}
              onDragEnd={onDragEnd}
              onOpen={onOpenPerson}
              onRemove={() => onRemove(person.leadId)}
            />
          </div>
        ))}
        {Array.from({ length: Math.max(0, turno.cupos - people.length) }).map(
          (_, i) => (
            <div key={i} className="assignment-board__free-slot">
              {t(`${i18nBase}.free_slot`)}
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default AssignmentTurnoBox;
